import ReactDOMServer from 'react-dom/server';

import { VARIABLES } from '@app/components/common/constants';

import { Run } from '../components/types';

export function kelvinToCelsius(kelvin: number): number {
  return kelvin - 273.15;
}

export function formatTime(
  time: string | Date,
  showTime: boolean = true,
  timezone: string = 'UTC',
): string {
  const date = typeof time === 'string' ? new Date(time) : time;

  if (timezone === 'UTC') {
    // Legacy UTC formatting
    if (showTime) {
      return `${date.getUTCDate()} ${date.toLocaleString('en-US', {
        month: 'short',
        timeZone: 'UTC',
      })} ${date.getUTCHours().toString().padStart(2, '0')}z`;
    }
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      timeZone: 'UTC',
    });
  } else {
    // Timezone-aware formatting
    if (showTime) {
      return date.toLocaleString('en-US', {
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        timeZone: timezone,
        timeZoneName: 'short',
      });
    }
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      timeZone: timezone,
    });
  }
}

export function getXAxisLabelConfig(
  selectedPreSelection?: 'intraday' | 'day-ahead' | 'forwards' | null,
  timezone: string = 'UTC',
) {
  switch (selectedPreSelection) {
    case 'intraday':
      return {
        tickInterval: 1,
        formatter: (value: string) => formatTime(value, true, timezone),
      };
    case 'day-ahead':
      return {
        tickInterval: 3,
        formatter: (value: string) => formatTime(value, true, timezone),
      };
    case 'forwards':
      return {
        tickInterval: 12,
        formatter: (value: string) => formatTime(value, true, timezone),
      };
    case null:
    default:
      return {
        tickInterval: 24,
        formatter: (value: string) => formatTime(value, true, timezone),
      };
  }
}

export function getVariableValue(point: any, variableId: string): number {
  const variable = VARIABLES.find((v) => v.id === variableId);
  if (!variable) return 0;

  let value = point[variable.juaId] as number;

  if (variable.id === '2t' && value > 100) {
    value = kelvinToCelsius(value);
  }

  return value;
}

export function getTooltipFormatter(variable: any, timezone: string = 'UTC') {
  return function (params: any[]) {
    // The inline html here has been copied mostly verbatim from ECharts' default tooltip markup.
    return ReactDOMServer.renderToString(
      <div>
        <div>{formatTime(params[0].axisValue, true, timezone)}</div>
        {params.map((param) => (
          <div
            style={{ margin: '10px 0 0', lineHeight: 1, opacity: param.data == null ? 0.66 : 1 }}
          >
            <div style={{ margin: '0px 0 0', lineHeight: 1 }}>
              <span
                style={{
                  display: 'inline-block',
                  marginRight: '4px',
                  borderRadius: '10px',
                  width: '10px',
                  height: '10px',
                  backgroundColor: param.color,
                }}
              ></span>
              <span style={{ fontSize: '14px', color: '#666', fontWeight: 400, marginLeft: '2px' }}>
                {param.seriesName}
              </span>
              <span
                style={{
                  float: 'right',
                  marginLeft: '20px',
                  fontSize: '14px',
                  color: '#666',
                  fontWeight: 900,
                }}
              >
                {param.data != null ? param.data.toLocaleString() : '-'}{' '}
                {variable ? variable.displayUnit : ''}
              </span>
              <div style={{ clear: 'both' }}></div>
            </div>
            <div style={{ clear: 'both' }}></div>
          </div>
        ))}
        <div style={{ clear: 'both' }}></div>
      </div>,
    );
  };
}

export function getCommonXAxis(
  xData: string[],
  selectedPreSelection: 'intraday' | 'day-ahead' | 'forwards' | null,
  axisColor: string,
  isDark: boolean,
  timezone: string = 'UTC',
) {
  const config = getXAxisLabelConfig(selectedPreSelection, timezone);
  return {
    type: 'category',
    data: xData,
    axisLabel: {
      color: axisColor,
      rotate: -30,
      fontSize: 10,
      formatter: config.formatter,
      interval: (index: number, time: string) => {
        const date = new Date(time);
        const hour =
          timezone === 'UTC'
            ? date.getUTCHours()
            : parseInt(
                date.toLocaleString('en-US', {
                  hour: '2-digit',
                  hour12: false,
                  timeZone: timezone,
                }),
              );
        if (selectedPreSelection) {
          return hour % config.tickInterval === 0;
        }
        return hour % 3 === 0;
      },
      hideOverlap: true,
    },
    splitLine: {
      show: true,
      alignWithLabel: true,
      lineStyle: {
        color: isDark ? '#374151' : '#E5E7EB',
        type: 'dashed',
      },
    },
    axisLine: {
      lineStyle: {
        color: axisColor,
      },
    },
    axisTick: {
      alignWithLabel: true,
      interval: (index: number, time: string) => {
        const date = new Date(time);
        const hour =
          timezone === 'UTC'
            ? date.getUTCHours()
            : parseInt(
                date.toLocaleString('en-US', {
                  hour: '2-digit',
                  hour12: false,
                  timeZone: timezone,
                }),
              );
        if (selectedPreSelection) {
          return hour % config.tickInterval === 0;
        }
        return hour % 3 === 0;
      },
    },
    animation: true,
    animationDuration: 300,
    animationEasing: 'cubicOut',
  };
}

export function getCommonGrid() {
  return { left: '10%', right: '10%', top: '15%', bottom: '20%' };
}

export function buildChartData(runs: Run[]): any[] {
  if (runs.length === 0) return [];
  const baselineRun = runs[0];
  const allTimes = new Set<string>();
  runs.forEach((run) => {
    run.data.forEach((point) => {
      allTimes.add(point.valid_time);
    });
  });
  const sortedTimes = Array.from(allTimes).sort();
  return sortedTimes.map((time) => {
    const result: any = { time };
    const baselinePoint = baselineRun.data.find((p) => p.valid_time === time);
    const baselineValue = baselinePoint
      ? getVariableValue(baselinePoint, baselineRun.variableId)
      : null;
    runs.forEach((run) => {
      const point = run.data.find((p) => p.valid_time === time);
      if (point) {
        const value = getVariableValue(point, run.variableId);
        result[`${run.id}_absolute`] = value;
        if (run.id !== baselineRun.id && baselineValue !== null) {
          result[`${run.id}_delta`] = value - baselineValue;
        } else {
          result[`${run.id}_delta`] = null;
        }
      } else {
        result[`${run.id}_absolute`] = null;
        result[`${run.id}_delta`] = null;
      }
    });
    return result;
  });
}

export function getAbsoluteSeries(chartData: any[], runs: Run[]): any[] {
  return runs.map((run, index) => {
    const seriesData = chartData.map((item) => item[`${run.id}_absolute`] ?? null);
    const color = index === 0 ? '#3B82F6' : `hsl(${(index * 137) % 360}, 65%, 55%)`;
    return {
      name: run.label,
      type: 'line',
      data: seriesData,
      yAxisIndex: 0,
      lineStyle: { width: index === 0 ? 2 : 1.5, color },
      itemStyle: { color },
      showSymbol: false,
      connectNulls: true,
    };
  });
}

export function getDeltaSeries(chartData: any[], runs: Run[]): any[] {
  const result: any[] = [];
  if (runs.length > 1) {
    runs.slice(1).forEach((run, index) => {
      const seriesData = chartData.map((item) => item[`${run.id}_delta`] ?? null);
      const color = `hsl(${((index + 1) * 137) % 360}, 65%, 55%)`;
      result.push({
        name: run.label,
        type: 'line',
        data: seriesData,
        yAxisIndex: 0,
        lineStyle: { width: 1.5, color },
        itemStyle: { color },
        areaStyle: { opacity: 0.3, color },
        showSymbol: false,
        connectNulls: true,
      });
    });
  }
  return result;
}

export function getDataZoomConfig(
  xData: string[],
  selectedPreSelection: 'intraday' | 'day-ahead' | 'forwards' | null,
  isDark: boolean = false,
): any[] {
  const baseConfig = {
    filterMode: 'none',
    start: 0,
    end: 100,
  };

  if (xData.length > 0) {
    if (selectedPreSelection) {
      const baselineTime = new Date(xData[0]);
      let targetHours = 0;
      switch (selectedPreSelection) {
        case 'intraday':
          targetHours = 24;
          break;
        case 'day-ahead':
          targetHours = 48;
          break;
        case 'forwards':
          targetHours = 480;
          break;
      }

      const targetTime = new Date(baselineTime);
      targetTime.setUTCHours(baselineTime.getUTCHours() + targetHours);
      const endIndex = xData.findIndex((time) => new Date(time) >= targetTime);
      if (endIndex !== -1) {
        baseConfig.end = (endIndex / xData.length) * 100;
      }
    }
  }

  return [
    {
      ...baseConfig,
      type: 'slider',
      show: true,
      xAxisIndex: [0],
      bottom: '2%',
      height: 20,
      borderColor: isDark ? '#374151' : '#E5E7EB',
      textStyle: {
        color: isDark ? '#E5E7EB' : '#666666',
      },
      handleStyle: {
        color: isDark ? '#4B5563' : '#9CA3AF',
        borderColor: isDark ? '#6B7280' : '#D1D5DB',
      },
      moveHandleStyle: {
        color: isDark ? '#4B5563' : '#9CA3AF',
      },
      selectedDataBackground: {
        lineStyle: {
          color: isDark ? '#6B7280' : '#D1D5DB',
        },
        areaStyle: {
          color: isDark ? '#4B5563' : '#F3F4F6',
        },
      },
    },
    {
      ...baseConfig,
      type: 'inside',
      xAxisIndex: [0],
      zoomOnMouseWheel: true,
      moveOnMouseMove: true,
    },
  ];
}
